/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { useDispatch } from 'react-redux';
import { mobileUtils } from '@blend/consumer-components';
import { ConnectOptions } from 'twilio-video';
import { useState, useCallback } from 'react';
import { VideoProviderType } from '../types';
import { useRootSelector } from '../../../hooks/useRootSelector';
import { addGenericErrorNotification } from '../../../actions/pageActions';
import { diagnostics } from '@utils/diagnostics';

const getVideoRoomError = (err: any) => {
  if (err.type && err.reason) {
    // handle Zoom client.join() errors
    return `Error connecting to the video room. \ntype: ${err.type}; reason: ${err.reason}`;
  } else {
    return `Error connecting to the video room.${err.message ?? ''}`;
  }
};

export function useVideoRoom(
  videoProvider: VideoProviderType,
  localTracks: (VideoProvider.ILocalAudioTrack | VideoProvider.ILocalVideoTrack)[],
  opts: ConnectOptions = {},
  connectWithTracks = true,
) {
  const dispatch = useDispatch();
  const [room, setRoom] = useState<VideoProvider.IRoom | null>(null);
  const [isConnecting, setIsConnecting] = useState(true);
  const [isVideoConnectionBroken, setIsVideoConnectionBroken] = useState(false);
  const { callDetails, user } = useRootSelector(state => ({
    callDetails: state.callDetails,
    user: state.user,
  }));

  const connect = useCallback(
    async (token: string, testSessionId?: string) => {
      if (isVideoConnectionBroken) {
        return;
      }
      setIsConnecting(true);
      try {
        const { disconnect, session: nextRoom } = await videoProvider.connect(token, () => setRoom(null), {
          twilioLocalTracks: connectWithTracks ? localTracks : [],
          opts,
          callId: callDetails?.id || testSessionId,
          userFullName: user?.fullName,
          videoRoomId: callDetails?.videoRoomId,
          isNotary: !!callDetails?.notaryVideoAccessToken,
        });

        // Note: Set as an empty object for Zoom to prevent direct SDK access; use/create abstract methods in VideoProvider instead
        setRoom(nextRoom);

        // Listen to the "beforeunload" event on window to leave the Room when the tab/browser is being closed.
        window.addEventListener('beforeunload', disconnect);

        if (mobileUtils.isMobileDevice()) {
          // iOS Safari does not emit the "beforeunload" event on window. Use "pagehide" instead.
          window.addEventListener('pagehide', disconnect);
        }

        setIsConnecting(false);
        // todo: handle mobile page hiding
      } catch (err) {
        /* eslint-disable no-console */
        console.error(`Fail to connect to ${videoProvider.name.toUpperCase()} room`, err);

        setIsConnecting(false);
        // TODO: let the notary know that Twilio connectivity is broken
        setIsVideoConnectionBroken(true);

        addGenericErrorNotification(getVideoRoomError(err), true)(dispatch);

        diagnostics.error(new Error('Failed to connect to video room'), err as Error);
        throw err;
      }
    },
    [localTracks, opts, connectWithTracks, isVideoConnectionBroken, callDetails?.id, user],
  );

  return { room, connect, isConnecting, isVideoConnectionBroken };
}
